import { amountToNumber, amountFormat } from '../../../../util/shared/vue-global';
import { toIsoDate, toShortDate } from '../../../../util/shared/tmc-global';
import { penaltyCalculation } from '../../common-utils/tax-common.util';

// ====== CONSTANTS ======
export const TAX_WARRANTS_BASE_CRUD_KEY = 'personalTaxWarrants';

export const TAX_WARRANT_DETAILS_BASE_CRUD_KEY = 'taxdetailsWarrantList';

export const TAX_WARRANT_CREATE_DIALOG_CRUD_KEY = 'personalTaxWarrantCreate';

export const TAX_WARRANT_DETAILS_CONF_DIALOG_CRUD_KEY = 'taxWarrantDetailsConfDialogCrudKey';

export const ATTACHMENT_CONSTANTS = {
  bucketName: 'warrant',
  program: 'warrant',
  parentObjectType: 'warrantTransaction',
};

export const UPLOAD_FILE_PARAMS = {
  bucketName: 'warrant',
  program: 'warrant',
  parentObjectType: 'warrantTransaction',
};

export const DEFAULT_SORT_BY = [
  {
    column: 'warrantNumber',
    direction: 'desc',
  },
];

// name1/name2/city/inCareOf1/inCareOf2/state/zip are the warrant's own editable header
// fields now (mirrored from the tax record at creation) and must reach the upsert.
// propertyOrParcelId stays tax-record-only, never sent as part of the warrant header.
export const EXCLUDED_KEYS = ['propertyOrParcelId', 'totalPaid', 'penaltyStartDate', 'penaltyAmt', 'totalDueWithFeesPenalty'];

export const FORMAT_KEYS = ['sherifFeeAmount', 'treasureFeeAmount', 'issueAmount', 'amountCollected'];

// Fields not allowed in taxWarrantDetailsInputFields GraphQL type
export const WARRANT_DETAILS_EXCLUDED_KEYS = ['city', 'address1', 'address2', 'address3', 'address4', 'propertyOrParcelId', 'totalPaid', 'penaltyStartDate'];

export const cleanWarrantDetailsForGraphQL = (warrantDetails) => {
  if (!warrantDetails || !Array.isArray(warrantDetails)) {
    return warrantDetails;
  }

  return warrantDetails.map((detail) => {
    const cleanDetail = { ...detail };

    // Remove fields not allowed in taxWarrantDetailsInputFields
    WARRANT_DETAILS_EXCLUDED_KEYS.forEach((field) => {
      delete cleanDetail[field];
    });

    return cleanDetail;
  });
};

export const WARRANT_NUMBER_MAX_LENGTH = 7;
export const formatWarrantNumber = (taxYear, warrantNumber) => {
  const yearPart = taxYear.toString().slice(-2);
  const warrantPartLength = WARRANT_NUMBER_MAX_LENGTH - yearPart.length;
  const warrantPart = warrantNumber.toString().padStart(warrantPartLength, '0');
  return amountToNumber(yearPart + warrantPart);
};

// The YY prefix of a NEW warrant number is always the county's CURRENT tax year - not the
// tax year of the record being warranted, and not whichever record happens to lead the
// selection. Tulsa 9/15/2026: a 2024 record sorted first and the whole 126-warrant batch
// printed as 24nnnnn while July's and August's were 25nnnnn. The tax config's own tax year
// is the county's "current year"; fall back to the newest tax year on file - the same
// `defaultTaxYearFromTaxSetting || latestTaxYear` order every tax screen already uses.
// Never the browser's calendar year: the prefix has to follow the county's roll, which
// turns over on its own schedule. Returns null when neither source has loaded yet, so
// callers can hold off rather than issue a 0-prefixed number.
export const getCurrentWarrantTaxYear = ({ taxSettingItem, latestTaxYear } = {}) => (
  Number((taxSettingItem || {}).taxYear) || Number(latestTaxYear) || null
);

// `taxYear` is the record the warrant is for (an old year is perfectly normal);
// `warrantTaxYear` is the current tax year that prefixes the number. They are deliberately
// separate - see getCurrentWarrantTaxYear.
export const createNewWarrantItem = ({
  taxYear,
  taxId,
  warrantTaxYear,
  nextWarrantNumber,
  nextWarrantSystemID,
  taxSettingItem,
}) => {
  const canNumber = warrantTaxYear
    && nextWarrantNumber !== undefined && nextWarrantNumber !== null;
  return {
    systemId: nextWarrantSystemID,
    sherifFee: (taxSettingItem.sherifFee || '').toString(),
    sherifFeeAmount: amountToNumber(taxSettingItem.sherifFeeAmount),
    treasureFee: (taxSettingItem.treasurerFee || '').toString(),
    treasureFeeAmount: amountToNumber(taxSettingItem.treasurerFeeAmount),
    // Left unset (rather than prefixed with a year we could not confirm) so the clerk sees
    // an empty Warrant No to fill in, instead of a wrong one that looks authoritative.
    ...(canNumber
      ? { warrantNumber: formatWarrantNumber(warrantTaxYear, nextWarrantNumber) }
      : {}),
    aliasNumber: 0,
    taxYear,
    taxId,
    issueDate: toIsoDate(new Date()),
    penaltyDate: toIsoDate(new Date()),
  };
};

export const processWarrantItemForUpsert = (item) => {
  const updatedItem = { ...item };

  // Remove excluded keys
  EXCLUDED_KEYS.forEach((key) => {
    delete updatedItem[key];
  });

  // Format amount fields
  FORMAT_KEYS.forEach((key) => {
    const value = updatedItem[key];
    // Only skip a field that's genuinely absent - 0 must persist so a
    // previously entered amount can be cleared back to zero.
    if (value === undefined || value === null || value === '') {
      // Remove the field from the object if it's not available
      delete updatedItem[key];
      return;
    }
    updatedItem[key] = amountToNumber(updatedItem[key]);
  });

  // Clean taxWarrantDetails to remove fields not allowed in GraphQL input
  if (updatedItem.taxWarrantDetails) {
    updatedItem.taxWarrantDetails = cleanWarrantDetailsForGraphQL(updatedItem.taxWarrantDetails);
  }

  return updatedItem;
};

export const validateWarrantItem = (item) => {
  if (!item.taxId || !item.taxYear) {
    return {
      isValid: false,
      message: 'Please select taxId and tax year!',
    };
  }

  if (item.amountCollected !== undefined && item.amountCollected !== null && item.amountCollected !== '') {
    // Number() (not amountToNumber, which silently coerces unparseable input to 0)
    // so genuinely non-numeric input is caught rather than passed through as 0.
    const rawAmountCollected = typeof item.amountCollected === 'string'
      ? item.amountCollected.replace(/,/g, '')
      : item.amountCollected;
    const amountCollectedNumber = Number(rawAmountCollected);
    if (Number.isNaN(amountCollectedNumber) || amountCollectedNumber < 0) {
      return {
        isValid: false,
        message: 'Amount Collected must be a valid, non-negative number.',
      };
    }
  }

  return {
    isValid: true,
    message: '',
  };
};

export const postProcessWarrantData = (items) => {
  const updatedItems = [];
  // propertyOrParcelId always reflects the tax record - it isn't a warrant field.
  const alwaysFromTaxInquiry = ['propertyOrParcelId'];
  // The warrant's own copy of these fields can be edited independently of the tax
  // inquiry record, so only default from the tax inquiry when the warrant doesn't
  // already have its own saved value (e.g. an older warrant predating the backfill).
  const ownFieldKeys = [
    'name1', 'name2', 'inCareOf1', 'inCareOf2',
    'address1', 'address2', 'address3', 'address4',
    'city', 'state', 'zip',
  ];

  for (let i = 0; i < items.length; i += 1) {
    const item = { ...items[i] };

    alwaysFromTaxInquiry.forEach((key) => {
      item[key] = (item.taxInquiryTransaction && item.taxInquiryTransaction[key]) || '';
    });

    ownFieldKeys.forEach((key) => {
      item[key] = item[key] || (item.taxInquiryTransaction && item.taxInquiryTransaction[key]) || '';
    });

    updatedItems.push(item);
  }

  return updatedItems;
};

export const urlEncodeObject = (obj) => new URLSearchParams(obj).toString();

export const getDialogWidth = (isNewItem) => (isNewItem ? '40vw' : '50vw');

export const getOwnerName = (item) => `${item.name1 || ''} ${item.name2 || ''}`;

export const createUploadFileClickParams = () => ({
  leftClickFlag: false,
  leftClickDef: undefined,
  rightClickFlag: false,
  rightClickDef: undefined,
});

export const createTaxInquiryGQLFields = () => ({
  _id: '',
  baseId: '',
  taxYear: '',
  taxId: '',
  taxType: '',
  address1: '',
  address2: '',
  address3: '',
  address4: '',
  totalPaid: '',
  totalTax: '',
  totalDue: '',
  totalFee: '',
  feesAndPen: '',
  penaltyStartDate: '',
  propertyOrParcelId: '',
});

export const createTaxWarrantDetailsGQLFields = () => ({
  baseId: 1,
  taxYear: 1,
  taxId: 1,
  totalDue: 1, // not sure we need this field or not
  totalPaid: 1,
  totalTax: 1,
  totalFee: 1,
  feesAndPen: 1,
  penaltyStartDate: 1,
  // penaltyDate: 1,
  // penDate: 1,
  // penaltyRate: 1,
  name1: 1,
  name2: 1,
  statusCode: 1,
});

export const validateWarrantItemWithTaxInquiry = async (
  item,
  context = null,
  taxSettingItem = {},
  warrantPenaltyDate = null,
) => {
  // Basic validation
  if (!item.taxId || !item.taxYear) {
    return {
      isValid: false,
      message: 'Please select taxId and tax year!',
    };
  }

  // Check if any required tax inquiry fields are missing
  const requiredFields = ['baseId', 'totalTax'];
  const missingFields = requiredFields.some((field) => item[field] === undefined || item[field] === null || item[field] === '');
  // If tax inquiry data is missing, try to fetch it
  if (missingFields) {
    if (!context) {
      return {
        isValid: false,
        message: 'Tax inquiry data missing (baseId, totalTax). Please ensure Tax ID and Tax Year are entered correctly.',
      };
    }

    if (!context.loadTaxInquires) {
      return {
        isValid: false,
        message: 'Unable to load tax inquiry data. loadTaxInquires function not available.',
      };
    }
    try {
      const data = await context.loadTaxInquires({
        criteria: { taxYear: item.taxYear, taxId: item.taxId },
        baseCrudKey: 'taxInquiryDataForTaxWarrantDetails',
        fields: createTaxWarrantDetailsGQLFields(),
      });
      if (data && data.length > 0) {
        const taxData = data[0];

        const { penaltyDate, penaltyRate } = taxSettingItem;

        // Use the warrant's own chosen Penalty Date when supplied, not today - the backend
        // recalculates authoritatively from that same date on save regardless, but the
        // preview shown here should match what will actually be persisted.
        const penDate = warrantPenaltyDate || toShortDate();
        // Calculate penalty amount
        const penaltyResults = penaltyCalculation(
          penDate,
          taxData.penaltyStartDate,
          penaltyDate,
          taxData.totalTax || 0,
          taxData.totalPaid || 0,
          taxData.totalFee || 0,
          penaltyRate,
          taxData.totalDue || 0,
          taxData.taxYear || item.taxYear,
          [], taxSettingItem,
        );

        const penaltyAmt = penaltyResults.penaltyAmt || 0;

        // Copy data to item (excluding fields that are not allowed in GraphQL input types)
        const fetchedData = {
          baseId: taxData.baseId,
          name1: taxData.name1,
          name2: taxData.name2,
          totalTax: taxData.totalTax,
          totalDue: taxData.totalDue,
          totalFee: taxData.totalFee,
          totalDueWithFeesPenalty: amountToNumber(
            (taxData.feesAndPen || 0),
          ),
          penaltyAmt: amountToNumber(penaltyAmt),
          // Exclude totalPaid and penaltyStartDate as they're not allowed in GraphQL input types
        };

        Object.assign(item, fetchedData);

        return {
          isValid: true,
          message: 'Tax inquiry data loaded successfully.',
          dataFetched: true,
          fetchedData, // Return the fetched data
        };
      }

      return {
        isValid: false,
        message: 'No tax record found for the provided Tax ID and Tax Year.',
      };
    } catch (error) {
      return {
        isValid: false,
        message: 'Error loading tax inquiry data. Please try again.',
      };
    }
  }

  return {
    isValid: true,
    message: '',
  };
};

export const getTaxInquiryKeys = () => [
  'propertyOrParcelId',
  'address1',
  'address2',
  'address3',
  'address4',
  'name1',
  'name2',
  'baseId',
  'totalDue',
  'totalFee',
  'totalTax',
];
export const createDefaultPenaltyDate = () => toIsoDate(new Date());
export const warrantAmountUtils = {
  formatAmount: (value) => amountFormat(value),
  toNumber: (value) => amountToNumber(value),
  roundAmount: (item, field) => {
    if (item[field] !== undefined && item[field] !== null) {
      return amountFormat(item[field]);
    }
    return item[field];
  },

  amountToNum: (item, field) => {
    if (item[field] !== undefined && item[field] !== null) {
      return amountToNumber(item[field]);
    }
    return item[field];
  },
};

export const TAX_WARRANTS_ACTIONS = [
  'loadTaxWarrants',
  'upsertWarrants',
  'loadLastWarrantNo',
  'loadLastSystemID',
  'deleteTaxWarrantDetailRow',
];

export const TAX_WARRANTS_MUTATIONS = [
  'setSelectedItems',
];

export const BASE_CRUD_MUTATIONS = [
  'setCriteria',
  'setItems',
  'setItem',
  'setIsVisible',
  'setIndex',
];

export const SYSTEM_CONFIG_ACTIONS = [
  'loadConfig',
];

export const GLOBAL_ACTIONS = [
  'reAuth',
  'custumColrFlashMsg',
  'flashWarn',
];

export const createDefaultComponentData = (baseCrudKey = TAX_WARRANTS_BASE_CRUD_KEY) => ({
  baseCrudKey,
  activeTransItem: undefined,
  attachmentConstants: ATTACHMENT_CONSTANTS,
  uploadFileParams: UPLOAD_FILE_PARAMS,
  uploadFileClickParams: createUploadFileClickParams(),
});

export const createDefaultComputedProperties = () => ({
  getActiveTransID() {
    return this.activeTransItem && this.activeTransItem._id;
  },

  isNewItem() {
    return this.index(this.baseCrudKey) === -1;
  },

  defaultSortBy() {
    return DEFAULT_SORT_BY;
  },

  dialogWidth() {
    return getDialogWidth(this.isNewItem);
  },
});
